import React, { PureComponent } from 'react';
import moment from 'moment';
import looppa from 'looppa';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import AnimatedCounter from 'components/animated-counter';
import t, { Trans } from 'translations';
import { DATETIME_LOCAL_MILLIS } from 'constants/timeformat';
import PageVisibility from 'components/page-visibility';

const TICKER_DELAY = 1000;

export default class Countdown extends PureComponent {
    static propTypes = {
        label: PropTypes.string,
        mustFreeze: PropTypes.bool,
        endDate: PropTypes.any,
        startDate: PropTypes.any,
        callback: PropTypes.func, // Is called when the countdown is done
        rootFacet: PropTypes.string,
        facet: PropTypes.string,
    };

    static defaultProps = {
        label: '',
        rootFacet: 'base',
    };

    static renderCounter(value, hasSeparator, translationPath, facet) {
        const nrToString = Math.abs(value) + ''; // eslint-disable-line
        const mustPad = nrToString.length === 1;
        const values = mustPad ? [0, nrToString[0]] : nrToString;
        return (
            <div className={classnames('countdown--display', {
                'countdown--display__has-separator': hasSeparator,
            })}>
                <div className="countdown--numbers" aria-hidden="true">
                    {looppa(values)((v, key, i) => (
                        <AnimatedCounter
                            ariaLabel={t(translationPath)}
                            key={i}
                            direction="up"
                            facet={classnames(facet)}
                            className={classnames('countdown--number', {
                                'countdown--number__has-margin': i !== values.length - 1,
                            })}
                            readonly={true}
                            amount={+v}
                        />
                    ))}
                    {hasSeparator && <div className="countdown--separator"/>}
                </div>
                <Trans className="countdown--counter-description" path={translationPath}/>
            </div>
        );
    }

    constructor(props) {
        super(props);
        this.state = this.parseProps(props);
        Object.assign(this.state, this.getDiff(), { visible: true }); // extend the initial state adding the diffing time result

        this.tick(true);
    }

    componentWillReceiveProps({ endDate, mustFreeze, startDate }) {
        if (mustFreeze) {
            this.stop();
            return;
        }

        if (this.props.endDate !== endDate || this.props.startDate !== startDate) {
            this.updateTimer(startDate, endDate);
        }
    }

    componentWillUnmount() {
        this.stop();
    }

    /**
     * Generate the diff time object used to update the counter displays
     * @returns {{days: number, hours: number, minutes: number, seconds: number}}
     */
    getDiff = () => {
        let diff = moment.duration(this.deltaTime > TICKER_DELAY ? this.deltaTime : 0);

        return {
            days: Math.floor(diff.asDays()),
            hours: diff.hours(),
            minutes: diff.minutes(),
            seconds: diff.seconds(),
        };
    };

    // countdown delta
    deltaTime = 0;

    handleVisibilityChange = (visibilityState) => {
        const isVisible = visibilityState === 'visible';
        this.setState({ visible: isVisible });
    };

    // cache the milliseconds offset on each timer update
    msSinceLastTick = 0;

    /**
     * Parse the component immutable props to generate the proper mutable component state counterpart
     * @param {*} endDate  - any valid moment js date
     * @param {*} startDate - any valid moment js date
     * @returns {{endDate: (*|moment.Moment), startDate: (*|moment.Moment)}}
     */
    parseProps = ({ endDate, startDate }) => {
        startDate = moment(startDate);
        endDate = moment(endDate);
        this.deltaTime = endDate.diff(startDate);
        return { startDate, endDate };
    };

    /**
     * Stop the ticks to restart maybe them afterwords
     */
    stop = () => {
        cancelAnimationFrame(this.rAF);
        clearTimeout(this.timer);
    };

    /**
     * Loop function that will be triggered every second to update the countdown numbers
     * @param {*} immediate  - Trigger a tick in the next event loop tick (less delayed)
     */
    tick = immediate => {
        this.msSinceLastTick = moment(); // cache the last tick start

        this.rAF = requestAnimationFrame(() => {
            this.timer = setTimeout(() => {
                this.deltaTime = this.deltaTime - (moment() - this.msSinceLastTick);
                this.updateDiff();
                if (this.deltaTime > 0) {
                    this.tick();
                } else if (!this.wasCallbackCalled) {
                    this.wasCallbackCalled = true;
                    this.props.callback && this.props.callback();
                }
            }, immediate ? 0 : TICKER_DELAY);
        });
    };

    // timer id that will be paired with to the setTimeout method
    timer = null;

    wasCallbackCalled = false;

    /**
     * It should be triggered on any tick to update the displays values
     */
    updateDiff = () => {
        this.setState(this.getDiff());
    };

    /**
     * Invoke an immediate tick after updating and stopping the current timer
     */
    updateTimer = (startDate = moment(), endDate = this.props.endDate) => {
        this.stop();

        const newState = this.parseProps({
            startDate,
            endDate,
        });

        this.setState(newState, () => this.tick(true));
    };

    render() {
        const { days, hours, minutes, seconds, visible } = this.state;
        const { label, rootFacet } = this.props;
        const inputValue = moment()
            .add(this.deltaTime)
            .format(DATETIME_LOCAL_MILLIS);

        return (
            <PageVisibility onChange={this.handleVisibilityChange}>
                <div className={classnames('countdown', `countdown__${ rootFacet }`)}>
                    <input type="datetime-local" className="countdown--hidden-input" aria-label={label}
                        aria-readonly="true" readOnly={true}
                        value={inputValue}/>
                    {(visible && days) ? Countdown.renderCounter(days, true, 'countdown.days', this.props.facet) : null}
                    {visible && Countdown.renderCounter(hours, true, 'countdown.hours', this.props.facet)}
                    {visible && Countdown.renderCounter(minutes, true, 'countdown.minutes', this.props.facet)}
                    {visible && Countdown.renderCounter(seconds, false, 'countdown.seconds', this.props.facet)}
                </div>
            </PageVisibility>
        );
    }
}



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/components/countdown/index.js